import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import AuthService from "../services/auth.service";
import DataService from "../services/data.service";
import {
  buildReportSummary,
  formatCurrency,
  formatDateLabel,
  toInputDateValue,
} from "../common/reporting";

const Home = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [adminMetrics, setAdminMetrics] = useState({ clients: 0, users: 0 });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  const currentMonthRange = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);

    return {
      fromDate: toInputDateValue(start),
      toDate: toInputDateValue(now),
    };
  }, []);

  useEffect(() => {
    const user = AuthService.getCurrentUser();

    if (!user) {
      navigate("/login");
      return;
    }

    setUserData(user);
  }, [navigate]);

  useEffect(() => {
    if (!userData) {
      return;
    }

    const isAdmin =
      userData.userRoles.includes("ADMIN") || userData.userRoles.includes("Admin");
    const clientCode =
      userData.clients && userData.clients[0] && userData.clients[0].companyCode;
    const clientPrimaryKeyId =
      userData.clients && userData.clients[0] && userData.clients[0].id;

    setLoading(true);
    setMessage("");

    if (isAdmin && !clientCode) {
      Promise.all([DataService.getAllClients(), DataService.getUsers()]).then(
        ([clientsResponse, usersResponse]) => {
          setAdminMetrics({
            clients: clientsResponse.data.length,
            users: usersResponse.data.length,
          });
          setRecentTransactions([]);
          setLoading(false);
        },
        (error) => {
          const responseMessage =
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString();
          setMessage(responseMessage);
          setLoading(false);
        }
      );

      return;
    }

    if (!clientCode || !clientPrimaryKeyId) {
      setLoading(false);
      return;
    }

    Promise.all([
      DataService.getClient(clientCode),
      DataService.fetchProjects(clientPrimaryKeyId),
      DataService.fetchDisbursementReport(
        currentMonthRange.fromDate,
        currentMonthRange.toDate,
        clientCode,
        "all",
        0
      ),
    ]).then(
      ([clientResponse, projectResponse, reportResponse]) => {
        const sortedTransactions = [...reportResponse.data].sort((left, right) => {
          return new Date(right.transactionDate) - new Date(left.transactionDate);
        });

        setClientData(clientResponse.data);
        setProjects(projectResponse.data);
        setRecentTransactions(sortedTransactions.slice(0, 5));
        setLoading(false);
      },
      (error) => {
        const responseMessage =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          error.toString();
        setMessage(responseMessage);
        setLoading(false);
      }
    );
  }, [currentMonthRange.fromDate, currentMonthRange.toDate, userData]);

  const summary = useMemo(() => buildReportSummary(recentTransactions), [recentTransactions]);
  const displayName =
    (userData && userData.user && userData.user.userName) ||
    (userData && userData.user && userData.user.normalizedUserName) ||
    "Payments team";
  const isAdmin =
    userData &&
    (userData.userRoles.includes("ADMIN") || userData.userRoles.includes("Admin"));
  const hasClient = userData && userData.clients && userData.clients[0];

  if (loading) {
    return (
      <div className="content-card content-card--padded content-card--centered">
        <Spinner animation="border" variant="primary" />
        <p className="content-card__supporting mb-0">
          Loading your payment operations overview...
        </p>
      </div>
    );
  }

  return (
    <div className="portal-page-stack">
      <section className="hero-banner">
        <div>
          <span className="portal-pill portal-pill--outline">Corporate payments workspace</span>
          <h3>Welcome back, {displayName}</h3>
          <p>
            This redesigned portal emphasizes transaction health, batch control,
            and client visibility so operations teams can move faster with more confidence.
          </p>
        </div>
        <div className="hero-banner__actions">
          <Link to="/processtransactions">
            <Button variant="light">Process a batch</Button>
          </Link>
          <Link to="/disbursementreport">
            <Button variant="outline-light">View reports</Button>
          </Link>
        </div>
      </section>

      {message ? <Alert variant="danger">{message}</Alert> : null}

      {isAdmin && !hasClient ? (
        <>
          <section className="metrics-grid">
            <article className="metric-card">
              <p className="metric-card__label">Managed clients</p>
              <h4>{adminMetrics.clients}</h4>
              <span>Accounts currently available in the corporate portfolio.</span>
            </article>
            <article className="metric-card">
              <p className="metric-card__label">Portal users</p>
              <h4>{adminMetrics.users}</h4>
              <span>Operators and administrators with portal access.</span>
            </article>
            <article className="metric-card">
              <p className="metric-card__label">Controls</p>
              <h4>Centralized</h4>
              <span>Use admin reports, client management, and user actions from one shell.</span>
            </article>
          </section>

          <section className="page-grid page-grid--two">
            <article className="content-card content-card--padded">
              <div className="section-heading">
                <div>
                  <p className="section-heading__eyebrow">Recommended next steps</p>
                  <h4>Use the redesigned admin workflow</h4>
                </div>
              </div>
              <div className="action-list">
                <Link to="/clients" className="action-list__item">
                  <span className="bi bi-buildings" aria-hidden="true" />
                  <div>
                    <strong>Review clients</strong>
                    <p>Check balances, account links, and onboarding details.</p>
                  </div>
                </Link>
                <Link to="/users" className="action-list__item">
                  <span className="bi bi-people" aria-hidden="true" />
                  <div>
                    <strong>Administer users</strong>
                    <p>Reset passwords, update phone numbers, and support operators.</p>
                  </div>
                </Link>
                <Link to="/adminreport" className="action-list__item">
                  <span className="bi bi-clipboard-data" aria-hidden="true" />
                  <div>
                    <strong>Open portfolio reporting</strong>
                    <p>Track cross-client transaction outcomes and investigate issues.</p>
                  </div>
                </Link>
              </div>
            </article>

            <article className="content-card content-card--padded content-card--dark">
              <div className="section-heading">
                <div>
                  <p className="section-heading__eyebrow">Trust and control</p>
                  <h4>Why this portal direction works</h4>
                </div>
              </div>
              <ul className="signal-list signal-list--light">
                <li>Dark navigation separates enterprise chrome from transactional work areas.</li>
                <li>Clear KPIs improve scan speed for operations and finance teams.</li>
                <li>Consistent cards, tables, and status treatments reinforce professionalism.</li>
              </ul>
            </article>
          </section>
        </>
      ) : hasClient ? (
        <>
          <section className="metrics-grid">
            <article className="metric-card">
              <p className="metric-card__label">Available balance</p>
              <h4>{formatCurrency(clientData && clientData.balance)}</h4>
              <span>Liquidity available for new payment instructions.</span>
            </article>
            <article className="metric-card">
              <p className="metric-card__label">Successful this month</p>
              <h4>{summary.successful}</h4>
              <span>Transactions completed successfully in the current month.</span>
            </article>
            <article className="metric-card">
              <p className="metric-card__label">Exceptions</p>
              <h4>{summary.failed}</h4>
              <span>Transactions needing review, follow-up, or retry attention.</span>
            </article>
            <article className="metric-card">
              <p className="metric-card__label">Active projects</p>
              <h4>{projects.length}</h4>
              <span>Programs available for payment allocation and reporting.</span>
            </article>
          </section>

          <section className="page-grid page-grid--two">
            <article className="content-card content-card--padded">
              <div className="section-heading">
                <div>
                  <p className="section-heading__eyebrow">Portfolio snapshot</p>
                  <h4>{clientData ? clientData.clientName : "Client overview"}</h4>
                </div>
                <span className="portal-pill portal-pill--outline">
                  {(userData.clients && userData.clients[0] && userData.clients[0].companyCode) ||
                    "No client code"}
                </span>
              </div>
              <div className="detail-grid">
                <div>
                  <span>Monthly payments</span>
                  <strong>{formatCurrency(summary.payments)}</strong>
                </div>
                <div>
                  <span>Monthly deposits</span>
                  <strong>{formatCurrency(summary.deposits)}</strong>
                </div>
                <div>
                  <span>Charges</span>
                  <strong>{formatCurrency(summary.charges)}</strong>
                </div>
                <div>
                  <span>Projects configured</span>
                  <strong>{projects.length}</strong>
                </div>
              </div>
            </article>

            <article className="content-card content-card--padded">
              <div className="section-heading">
                <div>
                  <p className="section-heading__eyebrow">Quick actions</p>
                  <h4>Move quickly without losing control</h4>
                </div>
              </div>
              <div className="action-list">
                <Link to="/processtransactions" className="action-list__item">
                  <span className="bi bi-cloud-arrow-up" aria-hidden="true" />
                  <div>
                    <strong>Upload a payment batch</strong>
                    <p>Validate and process disbursement files through the new guided flow.</p>
                  </div>
                </Link>
                <Link to="/disbursementreport" className="action-list__item">
                  <span className="bi bi-receipt-cutoff" aria-hidden="true" />
                  <div>
                    <strong>Review transaction outcomes</strong>
                    <p>Filter by date, phone, project, and investigate failed payments.</p>
                  </div>
                </Link>
                <Link to="/projects" className="action-list__item">
                  <span className="bi bi-kanban" aria-hidden="true" />
                  <div>
                    <strong>Manage projects</strong>
                    <p>Keep reporting aligned to internal campaigns and business lines.</p>
                  </div>
                </Link>
              </div>
            </article>
          </section>

          <section className="content-card content-card--padded">
            <div className="section-heading">
              <div>
                <p className="section-heading__eyebrow">Recent activity</p>
                <h4>Latest transactions</h4>
              </div>
              <Link to="/disbursementreport" className="section-link">
                Open full report
              </Link>
            </div>
            {recentTransactions.length ? (
              <div className="table-responsive portal-table-wrap">
                <table className="table portal-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Phone</th>
                      <th>Amount</th>
                      <th>Type</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTransactions.map((transaction) => (
                      <tr key={transaction.uniqueId}>
                        <td>{transaction.customerName}</td>
                        <td>{transaction.phoneNumber}</td>
                        <td>{formatCurrency(transaction.amount)}</td>
                        <td>{transaction.transactionType}</td>
                        <td>{formatDateLabel(transaction.transactionDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <span className="bi bi-inbox" aria-hidden="true" />
                <h5>No recent transactions yet</h5>
                <p>
                  As batches are processed, this dashboard will highlight recent activity and
                  make operational exceptions easier to spot.
                </p>
              </div>
            )}
          </section>
        </>
      ) : (
        <section className="empty-state empty-state--large">
          <span className="bi bi-person-x" aria-hidden="true" />
          <h4>No client account is linked to this user yet</h4>
          <p>
            Contact your administrator so your portal account can be linked to the right
            client profile before you start processing payments.
          </p>
        </section>
      )}
    </div>
  );
};

export default Home;
